import { REGIONS } from 'v2/configs/constants'
import type { RLanguage, RProviders, RVoice } from 'v2/pages/flow/sidebar/types'
import {
  ElevenLabsModels,
  getVoiceAccentForLanguage,
  ModelTypes,
  ProviderTypes,
} from '../config/agent-settings.validation.config'

// Fallback TTS model for Sarvam voices when the providers payload does not carry
// a model list for the provider.
export const SARVAM_TTS_MODEL = 'bulbul:v3'
export const NIMBLE_TTS_MODEL = 'nimble'

// Languages that carry a Beta tag in the agent language picker until GA. Delete this
// constant to remove every Beta tag at once.
export const BETA_VOICE_LANGUAGES = ['mr', 'kn', 'te', 'bn', 'gu', 'ta', 'es']

export const isBetaVoiceLanguage = (code: string): boolean => BETA_VOICE_LANGUAGES.includes(code)

// The Indic languages en-IN may pair with, one at a time.
const INDIC_WITH_INDIAN_ENGLISH = ['hi', 'mr', 'ta', 'te', 'kn', 'bn', 'gu']

// Every multi-language selection the picker allows (product decision, SER-6543). A
// selection of 0 or 1 distinct codes is always allowed; 2+ distinct codes must match one of
// these sets exactly (order and duplicates ignored).
export const ALLOWED_LANGUAGE_SETS: string[][] = [
  ['en-US', 'es'],
  ...INDIC_WITH_INDIAN_ENGLISH.map((code) => ['en-IN', code]),
]

// An en-IN + Indic agent speaks the Indic language, so its voice list is that language's.
export const voiceListLanguage = (language: string): string => {
  const codes = [...new Set(language.split(',').filter(Boolean))]
  const indic = codes.filter((code) => INDIC_WITH_INDIAN_ENGLISH.includes(code))
  return codes.length === 2 && codes.includes('en-IN') && indic.length === 1 ? indic[0] : language
}

const languageSetKey = (codes: string[]): string => [...new Set(codes)].sort().join(',')

const ALLOWED_LANGUAGE_SET_KEYS = new Set(ALLOWED_LANGUAGE_SETS.map(languageSetKey))

export const isLanguageSelectionAllowed = (languageCodes: string[]): boolean => {
  if (new Set(languageCodes).size <= 1) return true
  return ALLOWED_LANGUAGE_SET_KEYS.has(languageSetKey(languageCodes))
}

/**
 * A language option is offered only when adding it keeps the whole selection within an
 * allowed set. An already-selected language is never disabled, otherwise it could not be
 * deselected.
 */
export const isLanguageOptionDisabled = (code: string, selectedLanguages: string[]): boolean => {
  if (selectedLanguages.includes(code)) return false
  return !isLanguageSelectionAllowed([...selectedLanguages, code])
}

/**
 * The already-selected languages that block `code` from being added: the whole current
 * selection when adding `code` would leave an allowed set, so the picker can name the real
 * combination instead of a blanket rule. Empty when `code` is a valid addition.
 */
export const getConflictingLanguages = (code: string, selectedLanguages: string[]): string[] => {
  const others = selectedLanguages.filter((selected) => selected !== code)
  return isLanguageSelectionAllowed([...others, code]) ? [] : others
}

// Priority order for language sorting in voice language pickers
const LANGUAGE_SORT_ORDER = [
  'en',
  'en-US',
  'en-IN',
  'hi',
  'mr',
  'kn',
  'te',
  'bn',
  'gu',
  'ta',
  'en-GB',
  'en-AU',
  'es',
  'fr',
  'ja',
  'ar',
]
// Hardcoded filtered language list for voice flows
export const SUPPORTED_LANGUAGES = [
  'en-IN',
  'en-GB',
  'en-AU',
  'en-US',
  'hi',
  'mr',
  'kn',
  'te',
  'bn',
  'gu',
  'es',
  'ta',
]

export const processLanguageSelection = (languages: string[], region: string): string[] => {
  if (!languages?.length) return []

  const processedLanguages = [...languages]
  const enIndex = processedLanguages.indexOf('en')

  if (enIndex !== -1) {
    const regionSpecificEn = region === REGIONS.IN ? 'en-IN' : 'en-US'

    if (processedLanguages.includes(regionSpecificEn)) {
      processedLanguages.splice(enIndex, 1)
    } else {
      processedLanguages[enIndex] = regionSpecificEn
    }
  }
  // Remove duplicates incase of existing region-specific English variants
  return processedLanguages.filter((lang, index, arr) => arr.indexOf(lang) === index)
}

// Sorts languages by the specified priority order: English, Hinglish, Hindi, Spanish, French, Japanese, Arabic
// This ensures the languages appear in the exact order requested for better UX in language pickers.
export const sortLanguages = (languages: RLanguage[]) =>
  [...languages].sort((a, b) => {
    const aIndex = LANGUAGE_SORT_ORDER.indexOf(a.code)
    const bIndex = LANGUAGE_SORT_ORDER.indexOf(b.code)

    if (aIndex > -1 && bIndex > -1) return aIndex - bIndex
    if (aIndex > -1) return -1
    if (bIndex > -1) return 1
    if (a.name < b.name) return -1
    if (a.name > b.name) return 1

    return 0
  })

// Helper function to filter voices based on language(s)
export const filterVoicesByLanguage = (voices: RVoice[], languageCodes: string | string[]) => {
  const languages = Array.isArray(languageCodes) ? languageCodes : [languageCodes]

  if (!languages.length) return voices

  const filteredVoices = new Set<RVoice>()

  for (const languageCode of languages) {
    const supportedAccents = getVoiceAccentForLanguage(languageCode)
    if (supportedAccents.length) {
      const languageVoices = voices.filter((voice) => supportedAccents.includes(voice.meta?.accent))
      languageVoices.forEach((voice) => {
        filteredVoices.add(voice)
      })
    }
  }

  return Array.from(filteredVoices).sort((a, b) => a.label.localeCompare(b.label))
}

export const getDefaultVoiceForLanguage = (
  voices: RVoice[],
  languageCode: string,
  allLanguages?: string[],
  region?: string,
) => {
  // For English + any other language, prioritize the non-English language's accent
  let targetLang = languageCode
  if (allLanguages && allLanguages.length > 1 && languageCode.includes('en')) {
    const nonEnglishLang = allLanguages.find((lang) => !lang.includes('en'))
    if (nonEnglishLang) targetLang = nonEnglishLang
  }

  // Use Indian voices for India region or Hindi language, otherwise filter by target language
  const isIndianEnglish = region === REGIONS.IN && languageCode.includes('en')
  const targetVoices = isIndianEnglish
    ? voices
        .filter((voice) => voice.meta?.accent === 'en-IN')
        .sort((a, b) => a.label.localeCompare(b.label))
    : filterVoicesByLanguage(voices, targetLang)

  return targetVoices.find((voice) => voice.meta?.gender === 'female') || targetVoices[0] || null
}

type LanguageCode = string

export interface VoiceAiConfig {
  sttProvider: string
  sttModel: string
  ttsProvider: string
  ttsModel: string
  defaultVoice: string
  defaultVoiceLabel: string
}

interface BaseVoiceAiConfig {
  sttProvider: string
  sttModel: string
  ttsProvider: string
  ttsModel: string
  defaultVoice: string
  defaultVoiceLabel: string
}

interface LanguageAnalysis {
  hasEnglish: boolean
  hasHindi: boolean
  hasArabic: boolean
  nonEnglishLanguages: string[]
}

/**
 * Analyzes the selected languages to determine their characteristics
 */
function analyzeLanguages(languages: LanguageCode[]): LanguageAnalysis {
  const isEnglish = (lang: string) => ['en', 'en-US', 'en-IN', 'en-GB', 'en-AU'].includes(lang)
  const isHindi = (lang: string) => lang === 'hi'
  const isArabic = (lang: string) => lang === 'ar'

  return {
    hasEnglish: languages.some(isEnglish),
    hasHindi: languages.some(isHindi),
    hasArabic: languages.some(isArabic),
    nonEnglishLanguages: languages.filter((l) => !isEnglish(l)),
  }
}

/**
 * Selects the appropriate STT and TTS configuration based on languages and region
 * Key logic:
 * - Arabic + other languages → No STT/TTS (not supported)
 * - Only Arabic language → Cartesia (Arabic support)
 * - US region → ElevenLabs (optimized for US region)
 * - India region → Sarvam (regional optimization for any language)
 * - Multiple or single languages (except Arabic combinations) → Nova-3 (supports multilingual or single language)
 */
// Factory function to create STT/TTS config
const createConfig = (
  sttProvider = '',
  sttModel = '',
  ttsProvider = ProviderTypes.ElevenLabs,
  ttsModel = ModelTypes.ElevenLabs,
): BaseVoiceAiConfig => ({
  sttProvider,
  sttModel,
  ttsProvider: sttProvider ? ttsProvider : '',
  ttsModel: sttProvider ? ttsModel : '',
  defaultVoice: '',
  defaultVoiceLabel: '',
})

function selectConfiguration(
  languages: LanguageCode[],
  region: string,
  analysis: LanguageAnalysis,
): BaseVoiceAiConfig {
  const { hasArabic } = analysis

  // Handle empty language array
  if (languages.length === 0) return createConfig()

  // Special case: If Arabic is present with other languages, no STT and no voice
  if (hasArabic && languages.length > 1) return createConfig()

  // Cartesia case: Only Arabic language
  if (languages.length === 1 && hasArabic) {
    return createConfig(
      ProviderTypes.Cartesia,
      ModelTypes.CartesiaSTT,
      ProviderTypes.Cartesia,
      ModelTypes.Cartesia,
    )
  }
  // Sarvam case: India region (for any language combination except Arabic+others)
  if (region === REGIONS.IN) return createConfig(ProviderTypes.Sarvam, ModelTypes.Sarvam)

  // Deepgram Nova 3 cases: All other cases (single or multiple languages, no Arabic)
  return createConfig(ProviderTypes.Deepgram, ModelTypes.DeepgramNova3)
}

/**
 * Resolves actual provider and model IDs using the providers data
 */
function resolveProviderDetails(
  config: BaseVoiceAiConfig,
  providers: RProviders | undefined,
  languages: LanguageCode[],
  region?: string,
): VoiceAiConfig {
  // Find actual STT provider and model IDs
  const sttProviderData = config.sttProvider
    ? providers?.stt_providers?.find((p) => p.id === config.sttProvider)
    : null
  const actualSttModel =
    sttProviderData?.models?.find((m) => m.id === config.sttModel)?.id || config.sttModel

  // Find actual TTS provider and model IDs
  const ttsProviderData = providers?.tts_providers?.find((p) => p.id === config.ttsProvider)
  const actualTtsModel =
    ttsProviderData?.models?.find((m) => m.id === config.ttsModel)?.id || config.ttsModel

  // Get the best default voice for the selected language (prioritizes female voices)
  const defaultVoiceForLanguage = languages[0]
    ? getDefaultVoiceForLanguage(ttsProviderData?.voices || [], languages[0], languages, region)
    : null

  const selectedVoice =
    defaultVoiceForLanguage ||
    (config.defaultVoice
      ? ttsProviderData?.voices?.find((v) => v.label === config.defaultVoice)
      : null)

  const returnConfig = {
    sttProvider: config.sttProvider,
    sttModel: config.sttProvider ? actualSttModel : '',
    ttsProvider: config.ttsProvider,
    ttsModel: actualTtsModel,
    defaultVoice: selectedVoice?.id || '',
    defaultVoiceLabel: selectedVoice?.label || '',
  }

  return returnConfig
}

/**
 * Determines the optimal STT and TTS configuration based on selected languages and user region
 */
export function getVoiceAiConfig(
  languages: LanguageCode[],
  region: string,
  providers?: RProviders,
): VoiceAiConfig {
  // Process languages to handle 'en' replacement and duplicates
  const processedLanguages = processLanguageSelection(languages, region)
  const analysis = analyzeLanguages(processedLanguages)
  const baseConfig = selectConfiguration(processedLanguages, region, analysis)
  return resolveProviderDetails(baseConfig, providers, processedLanguages, region)
}

/**
 * Find TTS provider, model and voice label for a given voice id
 */
export function findTtsInfoByVoiceId(
  providers: RProviders | undefined,
  voiceId: string | undefined,
): { providerId: string; modelId: string; voiceLabel: string } | null {
  if (!providers || !voiceId) return null

  const ttsProvider = providers.tts_providers?.find((provider) =>
    (provider.voices || []).some((voice) => voice.id === voiceId),
  )
  if (!ttsProvider) return null

  const matchedVoice = (ttsProvider.voices || []).find((voice) => voice.id === voiceId)
  if (!matchedVoice) return null

  // Only a default-flagged model is trustworthy here: the models array is in
  // ai_models id order, so "first in list" resolves to the OLDEST model the
  // provider ever had (a deprecated bulbul:v2 killed prod calls this way).
  const defaultModel = ttsProvider.models?.find((model) => model.default && model.id)

  return {
    providerId: ttsProvider.id,
    modelId: defaultModel?.id || '',
    voiceLabel: matchedVoice.label,
  }
}

/**
 * Resolves the TTS provider for a selected voice: prefers the providers payload
 * (which knows the true provider of a voice), then the voice row's own provider.
 */
export function getVoiceProvider(
  providers: RProviders | undefined,
  voices: RVoice[] | undefined,
  voiceId: string | undefined,
): string {
  if (!voiceId) return ''
  const ttsInfo = findTtsInfoByVoiceId(providers, voiceId)
  if (ttsInfo) return ttsInfo.providerId
  return voices?.find((v) => v.id === voiceId)?.provider ?? ''
}

// Nimble voices are gated per org; a flow that already saved one keeps seeing it.
export const filterGatedVoices = (
  voices: RVoice[] | undefined,
  nimbleEnabled: boolean,
  savedVoiceId: string | undefined,
): RVoice[] | undefined =>
  nimbleEnabled
    ? voices
    : voices?.filter((v) => v.provider !== ProviderTypes.Nimble || v.id === savedVoiceId)

/**
 * Resolves the TTS model for a non-ElevenLabs voice. ElevenLabs voices are driven
 * by the Voice Model radio group, so this is only meaningful for other providers.
 * The voice row's own model (stamped per-voice by the catalog) wins; the
 * providers payload only supplies a default-flagged model as fallback.
 */
export function getTtsModelForVoice(
  providers: RProviders | undefined,
  voices: RVoice[] | undefined,
  voiceId: string | undefined,
): string {
  if (!voiceId) return ''
  const voiceRow = voices?.find((v) => v.id === voiceId)
  if (voiceRow?.model) return voiceRow.model
  const ttsInfo = findTtsInfoByVoiceId(providers, voiceId)
  if (ttsInfo?.modelId) return ttsInfo.modelId
  const provider = getVoiceProvider(providers, voices, voiceId)
  if (provider === ProviderTypes.Sarvam) return SARVAM_TTS_MODEL
  if (provider === ProviderTypes.Nimble) return NIMBLE_TTS_MODEL
  return ''
}

const ELEVENLABS_MODEL_IDS: string[] = [ElevenLabsModels.TurboV2_5, ElevenLabsModels.FlashV2_5]

/**
 * Single source of the invariant "the saved TTS model must match its voice's provider".
 * A non-ElevenLabs provider (Sarvam, Cartesia) has no Voice Model radio, so the voice's
 * provider dictates the model and any stale model is overwritten. ElevenLabs voices keep
 * the radio-driven model when it is a valid ElevenLabs id, otherwise fall back to the
 * ElevenLabs default. Every writer that changes the voice/provider must route through here
 * so provider and model can never disagree in a saved config.
 */
export function resolveTtsModelForVoice(
  providers: RProviders | undefined,
  voices: RVoice[] | undefined,
  voiceId: string | undefined,
  currentModel?: string,
): string {
  const provider = getVoiceProvider(providers, voices, voiceId)
  if (provider && provider !== ProviderTypes.ElevenLabs) {
    return getTtsModelForVoice(providers, voices, voiceId)
  }
  return currentModel && ELEVENLABS_MODEL_IDS.includes(currentModel)
    ? currentModel
    : ElevenLabsModels.TurboV2_5
}

export interface VoiceSpeedBounds {
  min: number
  max: number
  step: number
}

const ELEVENLABS_SPEED_BOUNDS: VoiceSpeedBounds = { min: 0.7, max: 1.2, step: 0.05 }
const SARVAM_SPEED_BOUNDS: VoiceSpeedBounds = { min: 0.5, max: 2.0, step: 0.05 }

/**
 * Speed/pace bounds are provider specific: Sarvam's bulbul pace accepts a wider
 * range than ElevenLabs' speed. Every non-Sarvam provider keeps the ElevenLabs bounds.
 */
export const getVoiceSpeedBounds = (provider: string | undefined): VoiceSpeedBounds =>
  provider === ProviderTypes.Sarvam ? SARVAM_SPEED_BOUNDS : ELEVENLABS_SPEED_BOUNDS

export const clampVoiceSpeed = (value: number, bounds: VoiceSpeedBounds): number =>
  Math.min(bounds.max, Math.max(bounds.min, value))
